import React, { useCallback, useEffect, useRef, useState } from "react";
import { collection, getDocs, setDoc, doc, onSnapshot, query, orderBy, where } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

const API_BASE = "https://kiranaos-api-1036279469035.asia-south1.run.app";

// ─── Tokens ───────────────────────────────────────────────────────────────────
const T = {
  font:      "'DM Sans', sans-serif",
  ink:       "#1A0A00",
  inkSoft:   "#6b5c4e",
  orange:    "#E8470A",
  offWhite:  "#F4F3F1",
  warmWhite: "#FFFFFF",
  border:    "#E8E4DE",
  green:     "#1c7c54",
  red:       "#c0392b",
  blue:      "#1e40af",
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: { fontFamily: T.font, background: T.offWhite, minHeight: "100vh", color: T.ink },

  // Header
  header: (mobile) => ({
    background: "#111", padding: mobile ? "12px 16px" : "14px 32px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    borderBottom: "1px solid #1e1e1e",
  }),
  headerLeft:  { display: "flex", alignItems: "center", gap: 10 },
  logo:        { fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: -0.3 },
  badge:       { fontSize: 10, background: T.orange, color: "#fff", borderRadius: 4, padding: "2px 8px", fontWeight: 700, letterSpacing: 0.5 },
  headerRight: (mobile) => ({ display: "flex", alignItems: "center", gap: mobile ? 8 : 14 }),
  headerEmail: (mobile) => ({ fontSize: 12, color: "#777", display: mobile ? "none" : "block" }),

  // Tabs
  tabs: (mobile) => ({
    display: "flex", gap: 0, background: T.warmWhite,
    borderBottom: `1px solid ${T.border}`,
    padding: mobile ? "0 14px" : "0 32px",
    overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none",
  }),
  tab: (active, mobile) => ({
    padding: mobile ? "12px 11px" : "13px 18px", fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? T.orange : "#999",
    borderBottom: `2px solid ${active ? T.orange : "transparent"}`,
    cursor: "pointer", background: "none", border: "none",
    fontFamily: T.font, whiteSpace: "nowrap", flex: "0 0 auto",
  }),

  // Body
  body: (mobile) => ({
    padding: mobile ? "16px 14px 40px" : "28px 32px 56px",
    maxWidth: 1440, margin: "0 auto",
  }),

  // Cards
  card: (mobile) => ({
    background: T.warmWhite, borderRadius: 12, border: `1px solid ${T.border}`,
    padding: mobile ? "16px 14px" : "20px 24px", marginBottom: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }),
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, marginBottom: 16,
  },
  cardMeta: { fontSize: 13, color: "#999", margin: 0 },

  // Tables
  table:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:     { textAlign: "left", padding: "9px 12px", borderBottom: `1px solid ${T.border}`, color: "#aaa", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, background: "#faf9f7" },
  td:     { padding: "11px 12px", borderBottom: `1px solid ${T.border}`, verticalAlign: "middle" },

  // Stat cards
  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 24 },
  statCard: { background: T.warmWhite, border: `1px solid ${T.border}`, borderRadius: 12, padding: "18px 20px", borderLeft: `3px solid ${T.orange}`, boxShadow: "0 1px 4px rgba(0,0,0,0.05)" },
  statNum:  { fontSize: 32, fontWeight: 700, color: T.ink, lineHeight: 1, letterSpacing: -1 },
  statLbl:  { fontSize: 11, color: "#aaa", marginTop: 7, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },

  // Buttons — named variants
  btn: (variant = "primary") => {
    const base = {
      padding: "5px 13px", borderRadius: 7, border: "none",
      cursor: "pointer", fontSize: 12, fontWeight: 600,
      fontFamily: T.font, lineHeight: "20px", display: "inline-block",
    };
    const variants = {
      primary:   { background: T.orange,  color: "#fff" },
      secondary: { background: "#f0ede9", color: T.inkSoft, border: `1px solid ${T.border}` },
      success:   { background: "#dcfce7", color: "#15803d" },
      danger:    { background: "#fee2e2", color: "#b91c1c" },
      ghost:     { background: "#f0ede9", color: "#777",    border: `1px solid ${T.border}` },
      action:    { background: "#eff6ff", color: T.blue },
    };
    return { ...base, ...(variants[variant] || variants.primary) };
  },

  // Misc
  link:        { color: T.orange, textDecoration: "none", fontWeight: 500 },
  spinner:     { textAlign: "center", padding: "64px 20px", color: "#ccc", fontSize: 13 },
  empty:       { textAlign: "center", padding: "40px 20px", color: "#ccc", fontSize: 13 },
  errorBanner: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#b91c1c", marginBottom: 14, lineHeight: 1.5 },
  select:      { fontFamily: T.font, fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 8px", background: "#faf9f7", color: T.ink, cursor: "pointer" },

  // Status pill helper
  pill: (bg, color) => ({ display: "inline-block", fontSize: 11, fontWeight: 600, borderRadius: 20, padding: "3px 9px", background: bg, color, lineHeight: 1.6 }),
};

const ONBOARDING_REJECTION_REASONS = [
  { label: "AI generated image", reason: "AI generated store image. Please upload a real shop image." },
  { label: "Wrong store image", reason: "Store image incorrect. Please upload the correct shop image." },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toDateSafe(ts) {
  if (!ts) return null;
  let d;
  if (typeof ts.toDate === "function") d = ts.toDate();
  else if (typeof ts._seconds === "number") d = new Date(ts._seconds * 1000);
  else if (typeof ts.seconds === "number") d = new Date(ts.seconds * 1000);
  else if (typeof ts === "string" || typeof ts === "number") d = new Date(ts);
  else return null;
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmt(ts) {
  const d = toDateSafe(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtAge(ts) {
  const d = toDateSafe(ts);
  if (!d) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtDate(ts) {
  const d = toDateSafe(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function normalizeLeadPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.slice(-10);
}

function timestampToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const date = new Date(ts);
  return Number.isNaN(date.getTime()) ? null : date;
}

function fmtMessageTime(ts) {
  const date = timestampToDate(ts);
  if (!date) return "";

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMessageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfMessageDay) / 86400000);
  const time = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function conversationActivityDate(convo) {
  return timestampToDate(convo.lastMessageAt)
    || timestampToDate(convo.updatedAt)
    || timestampToDate(convo.createdAt)
    || new Date(0);
}

function useMediaQuery(queryText) {
  const getMatches = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(queryText).matches;
  };
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia(queryText);
    const update = () => setMatches(query.matches);
    update();
    if (query.addEventListener) {
      query.addEventListener("change", update);
      return () => query.removeEventListener("change", update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, [queryText]);

  return matches;
}

// ─── OnboardingTab ───────────────────────────────────────────────────────────
function OnboardingTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectionSelections, setRejectionSelections] = useState({});

  const getToken = useCallback(async () => {
    const currentUser = (auth || getAuth()).currentUser;
    const token = await currentUser?.getIdToken();
    if (!token) throw new Error("Admin auth missing");
    return token;
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/onboarding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load onboarding requests");
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message || "Failed to load onboarding requests");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const approve = async (request) => {
    if (!window.confirm(`Approve "${request.storeName}"? This will create the store and send the links on WhatsApp.`)) return;
    setBusyId(request.id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/onboarding/${request.id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Approval failed");
      await loadRequests();
    } catch (err) {
      setError(err.message || "Approval failed");
    } finally {
      setBusyId("");
    }
  };

  const reject = async (request) => {
    const reason = rejectionSelections[request.id];
    if (!reason || !reason.trim()) return;
    if (!window.confirm(`Reject "${request.storeName}" with reason:\n\n"${reason}"`)) return;
    setBusyId(request.id);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/onboarding/${request.id}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rejectionReason: reason.trim() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Rejection failed");
      await loadRequests();
    } catch (err) {
      setError(err.message || "Rejection failed");
    } finally {
      setBusyId("");
    }
  };

  if (loading) return <div style={S.spinner}>Loading onboarding requests…</div>;

  const statusPill = (status) => {
    const s = status || "pending";
    const map = {
      pending:  ["#FDECE4", T.orange],
      approved: ["#dcfce7", T.green],
      rejected: ["#fee2e2", T.red],
      cancelled: ["#f3f4f6", "#6b7280"],
    };
    const [bg, color] = map[s] || map.pending;
    return S.pill(bg, color);
  };

  return (
    <div style={S.card(isMobile)}>
      <div style={S.cardHeader}>
        <p style={S.cardMeta}>{requests.length} onboarding requests</p>
        <button style={S.btn("ghost")} type="button" onClick={loadRequests}>Refresh</button>
      </div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Submitted", "Store", "Owner", "Phone", "Location", "Business IDs", "Status", "Actions"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, ...S.empty }}>No onboarding requests yet</td></tr>
            )}
            {requests.map((r) => (
              <tr key={r.id}>
                <td style={{ ...S.td, color: "#999", fontSize: 12, whiteSpace: "nowrap" }}>
                  {fmt(r.createdAt)}
                  {(r.status || "pending") === "pending" && fmtAge(r.createdAt) && (
                    <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2, color: (Date.now() - (toDateSafe(r.createdAt)?.getTime() || Date.now())) > 86400000 ? "#b45309" : "#aaa" }}>
                      {fmtAge(r.createdAt)}
                    </div>
                  )}
                </td>
                <td style={S.td}>
                  <div style={{ fontWeight: 700 }}>{r.storeName || "-"}</div>
                  {r.storePhotoUrl && <a style={{ ...S.link, fontSize: 12 }} href={r.storePhotoUrl} target="_blank" rel="noreferrer">Photo ↗</a>}
                  {r.createdStoreId && <div style={{ fontSize: 11, color: "#aaa", marginTop: 3, fontFamily: "monospace" }}>{r.createdStoreId}</div>}
                </td>
                <td style={S.td}>{r.ownerName || "-"}</td>
                <td style={{ ...S.td, whiteSpace: "nowrap" }}>{r.phone || "-"}</td>
                <td style={{ ...S.td, fontSize: 12 }}>
                  {[r.address?.city, r.address?.state, r.address?.pincode].filter(Boolean).join(", ") || "-"}
                </td>
                <td style={S.td}>
                  <div style={{ fontSize: 11, color: "#888", lineHeight: 1.8 }}>
                    {r.fssaiNumber && <div>FSSAI: {r.fssaiNumber}</div>}
                    {r.gstin && <div>GSTIN: {r.gstin}</div>}
                    {r.udyamNumber && <div>Udyam: {r.udyamNumber}</div>}
                    {!r.fssaiNumber && !r.gstin && !r.udyamNumber && "—"}
                  </div>
                </td>
                <td style={S.td}>
                  <span style={statusPill(r.status)}>{r.status || "pending"}</span>
                  {r.rejectionReason && <div style={{ color: "#b91c1c", fontSize: 11, marginTop: 5, maxWidth: 160 }}>{r.rejectionReason}</div>}
                </td>
                <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                  {r.status === "pending" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 7, alignItems: "stretch", width: isMobile ? 168 : 196 }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          style={{ ...S.btn("success"), flex: 1, minHeight: isMobile ? 38 : 30, opacity: busyId === r.id ? 0.6 : 1 }}
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => approve(r)}
                        >
                          {busyId === r.id ? "…" : "Approve"}
                        </button>
                        <button
                          style={{ ...S.btn("danger"), flex: 1, minHeight: isMobile ? 38 : 30, opacity: busyId === r.id || !rejectionSelections[r.id] ? 0.45 : 1, cursor: rejectionSelections[r.id] ? "pointer" : "not-allowed" }}
                          type="button"
                          disabled={busyId === r.id || !rejectionSelections[r.id]}
                          title={rejectionSelections[r.id] ? "Reject with the selected reason" : "Select a rejection reason first"}
                          onClick={() => reject(r)}
                        >
                          Reject
                        </button>
                      </div>
                      <select
                        style={{ ...S.select, width: "100%", minHeight: isMobile ? 38 : 28, fontSize: isMobile ? 13 : 12, textOverflow: "ellipsis", color: rejectionSelections[r.id] ? T.ink : "#999" }}
                        value={rejectionSelections[r.id] || ""}
                        onChange={(e) => setRejectionSelections((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      >
                        <option value="">Rejection reason…</option>
                        {ONBOARDING_REJECTION_REASONS.map(({ label, reason }) => (
                          <option key={reason} value={reason}>{label}</option>
                        ))}
                      </select>
                      {rejectionSelections[r.id] && (
                        <div style={{ fontSize: 11, lineHeight: 1.5, color: "#888", whiteSpace: "normal" }}>
                          Will send: “{rejectionSelections[r.id]}”
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: "#bbb", fontSize: 12 }}>Reviewed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── StoresTab ────────────────────────────────────────────────────────────────
function StoresTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [stores, setStores]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(collection(db, "stores"))
      .then((snap) => {
        setStores(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const togglePause = async (storeId, current) => {
    const next = !current;
    setStores((prev) => prev.map((s) => s._id === storeId ? { ...s, storePaused: next } : s));
    await setDoc(doc(db, "stores", storeId), { storePaused: next }, { merge: true });
  };

  const deleteStore = async (storeId) => {
    // Step 1: explain the full blast radius.
    if (!window.confirm(
      `Permanently delete store "${storeId}"?\n\n` +
      `This will permanently delete the store doc, products, orders, customers, ` +
      `ratings, onboarding profile, and owner login.\n\n` +
      `If this store was created from onboarding, the linked onboarding request will ` +
      `be marked cancelled for audit.\n\n` +
      `This cannot be undone.`
    )) return;

    // Step 2: require the admin to type the exact storeId. This is the real
    // guard: it defends against deleting the WRONG store, not just stray clicks.
    const typed = window.prompt(
      `Are you absolutely sure?\n\n` +
      `Deleted stores cannot be recovered from the dashboard.\n\n` +
      `To confirm, type the exact store ID below:\n${storeId}`
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== storeId) {
      alert(`Store ID did not match ("${typed.trim()}" != "${storeId}"). Deletion cancelled.`);
      return;
    }

    try {
      const currentUser = (auth || getAuth()).currentUser;
      if (!currentUser) { alert("Admin login required."); return; }
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/admin/stores/${encodeURIComponent(storeId)}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Delete failed");
      const { counts } = data;
      alert(
        `Store "${storeId}" fully deleted.\n` +
        `Products: ${counts.products}  Orders: ${counts.orders}  Customers: ${counts.customers}  ` +
        `Ratings: ${counts.ratings}  Onboarding: ${counts.onboarding}\n` +
        `Linked onboarding requests cancelled: ${data.onboardingReconciled || 0}`
      );
      setStores((prev) => prev.filter((s) => s._id !== storeId));
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const viewStoreDashboard = async (storeId, storeName) => {
    try {
      const currentUser = (auth || getAuth()).currentUser;
      const token = await currentUser?.getIdToken();
      const res = await fetch("https://kiranaos-api-1036279469035.asia-south1.run.app/api/admin/impersonate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      sessionStorage.setItem("kiranaos_admin_impersonate", JSON.stringify({
        adminMode: true,
        targetStoreId: storeId,
        targetStoreName: storeName,
        activeTab: "inventory",
        stores: data.stores,
      }));
      window.open("/dashboard", "_blank");
    } catch (err) {
      alert("Failed to open dashboard: " + err.message);
    }
  };

  if (loading) return <div style={S.spinner}>Loading stores…</div>;

  return (
    <div style={S.card(isMobile)}>
      <p style={{ ...S.cardMeta, marginBottom: 16 }}>{stores.length} stores</p>
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Store ID", "Store Name", "Phone", "Address", "Status", "Actions"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stores.length === 0 && (
              <tr><td colSpan={6} style={{ ...S.td, ...S.empty }}>No stores yet</td></tr>
            )}
            {stores.map((s) => (
              <tr key={s._id}>
                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12, color: "#888" }}>{s._id}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{s.storeName || s.name || "—"}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{s.phone || "—"}</td>
                <td style={{ ...S.td, fontSize: 12, color: "#888" }}>{s.address || "—"}</td>
                <td style={S.td}>
                  <span style={S.pill(s.storePaused ? "#fee2e2" : "#dcfce7", s.storePaused ? T.red : T.green)}>
                    {s.storePaused ? "paused" : "active"}
                  </span>
                </td>
                <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={S.btn(s.storePaused ? "success" : "ghost")} onClick={() => togglePause(s._id, s.storePaused)}>
                      {s.storePaused ? "Unpause" : "Pause"}
                    </button>
                    <button style={S.btn("action")} onClick={() => viewStoreDashboard(s._id, s.storeName || s.name || s._id)}>
                      View
                    </button>
                    <button style={S.btn("danger")} onClick={() => deleteStore(s._id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── CustomersTab ─────────────────────────────────────────────────────────────
function CustomersTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [storeData, setStoreData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const storeSnap = await getDocs(collection(db, "stores"));
        const data = await Promise.all(
          storeSnap.docs.map(async (storeDoc) => {
            const storeId = storeDoc.id;
            const storeName = storeDoc.data().storeName || storeId;

            // Get all customers for this store
            const customersSnap = await getDocs(collection(db, "stores", storeId, "customers"));
            const totalCustomers = customersSnap.size;

            // Calculate active customers (last 30 days)
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            let activeCustomers = 0;
            let lastCustomerLogin = null;

            customersSnap.forEach((customerDoc) => {
              const customerData = customerDoc.data();
              const lastSeen = customerData.lastSeenAt;

              if (lastSeen) {
                const lastSeenDate = lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen);

                // Count active customers
                if (lastSeenDate >= thirtyDaysAgo) {
                  activeCustomers++;
                }

                // Track most recent login
                if (!lastCustomerLogin || lastSeenDate > lastCustomerLogin) {
                  lastCustomerLogin = lastSeenDate;
                }
              }
            });

            return {
              storeId,
              storeName,
              totalCustomers,
              activeCustomers,
              lastCustomerLogin,
            };
          })
        );

        // Sort by total customers descending
        data.sort((a, b) => b.totalCustomers - a.totalCustomers);
        setStoreData(data);
      } catch (err) {
        console.error("CustomersTab error:", err);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={S.spinner}>Loading customers…</div>;

  const totalCustomers = storeData.reduce((sum, s) => sum + s.totalCustomers, 0);
  const totalActive = storeData.reduce((sum, s) => sum + s.activeCustomers, 0);

  return (
    <div>
      <div style={S.statGrid}>
        <div style={S.statCard}>
          <div style={S.statNum}>{totalCustomers}</div>
          <div style={S.statLbl}>Total Customers</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statNum}>{totalActive}</div>
          <div style={S.statLbl}>Active (30d)</div>
        </div>
      </div>

      <div style={S.card(isMobile)}>
        <p style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600 }}>Customers by Store</p>
        {storeData.length === 0 ? (
          <div style={S.empty}>No customer data yet</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Store</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Total</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Active (30d)</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {storeData.map((store) => (
                  <tr key={store.storeId}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{store.storeName}</div>
                      <div style={{ fontSize: 11, color: "#aaa", fontFamily: "monospace" }}>{store.storeId}</div>
                    </td>
                    <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{store.totalCustomers}</td>
                    <td style={{ ...S.td, textAlign: "right", color: store.activeCustomers > 0 ? T.green : "#bbb" }}>{store.activeCustomers}</td>
                    <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: "#999" }}>
                      {store.lastCustomerLogin ? fmtDate(store.lastCustomerLogin) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── OrdersTab ────────────────────────────────────────────────────────────────
function OrdersTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const storeSnap = await getDocs(collection(db, "stores"));
        const all = [];
        await Promise.all(storeSnap.docs.map(async (storeDoc) => {
          const ordSnap = await getDocs(collection(db, "stores", storeDoc.id, "orders"));
          ordSnap.docs.forEach((o) => all.push({ _storeId: storeDoc.id, _id: o.id, ...o.data() }));
        }));
        all.sort((a, b) => {
          const ta = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : 0;
          const tb = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : 0;
          return tb - ta;
        });
        setOrders(all);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={S.spinner}>Loading orders…</div>;

  const orderStatusPill = (status) => {
    const map = {
      delivered:       ["#dcfce7", T.green],
      cancelled:       ["#fee2e2", T.red],
      pending:         ["#fef9c3", "#854d0e"],
      accepted:        ["#eff6ff", T.blue],
      preparing:       ["#fff7ed", "#c2410c"],
      out_for_delivery:["#f0fdf4", "#15803d"],
    };
    const [bg, color] = map[status] || ["#f4f3f1", "#888"];
    return S.pill(bg, color);
  };

  return (
    <div style={S.card(isMobile)}>
      <p style={{ ...S.cardMeta, marginBottom: 16 }}>{orders.length} orders across all stores</p>
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Store", "Order ID", "Customer", "Total", "Status", "Date"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={6} style={{ ...S.td, ...S.empty }}>No orders yet</td></tr>
            )}
            {orders.map((o) => (
              <tr key={`${o._storeId}-${o._id}`}>
                <td style={{ ...S.td, fontSize: 12, fontFamily: "monospace", color: "#888" }}>{o._storeId}</td>
                <td style={{ ...S.td, fontFamily: "monospace", fontSize: 11, color: "#bbb" }}>{o._id.slice(0, 10)}…</td>
                <td style={{ ...S.td, fontSize: 12 }}>{o.customerPhone || o.phone || "—"}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{o.total != null ? `₹${o.total}` : "—"}</td>
                <td style={S.td}>
                  <span style={orderStatusPill(o.status)}>{o.status || "—"}</span>
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#999" }}>{fmtDate(o.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── AnalyticsTab ─────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [storeSnap, leadsSnap] = await Promise.all([
          getDocs(collection(db, "stores")),
          getDocs(collection(db, "leads")),
        ]);

        let totalOrders = 0;
        await Promise.all(storeSnap.docs.map(async (storeDoc) => {
          const ordSnap = await getDocs(collection(db, "stores", storeDoc.id, "orders"));
          totalOrders += ordSnap.size;
        }));

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const newLeadsToday = leadsSnap.docs.filter((d) => {
          const ts = d.data().createdAt;
          if (!ts) return false;
          const date = ts.toDate ? ts.toDate() : new Date(ts);
          return date >= today;
        }).length;

        const uniqueLeadPhones = new Set(
          leadsSnap.docs
            .map((d) => normalizeLeadPhone(d.data().phone))
            .filter(Boolean)
        );

        setStats({
          totalStores: storeSnap.size,
          totalLeads:  uniqueLeadPhones.size,
          totalOrders,
          newLeadsToday,
        });
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={S.spinner}>Loading analytics…</div>;
  if (!stats)  return <div style={S.spinner}>Failed to load analytics.</div>;

  const cards = [
    { label: "Total Stores",    value: stats.totalStores },
    { label: "Total Leads",     value: stats.totalLeads },
    { label: "Total Orders",    value: stats.totalOrders },
    { label: "New Leads Today", value: stats.newLeadsToday },
  ];

  return (
    <div>
      <div style={S.statGrid}>
        {cards.map((c) => (
          <div key={c.label} style={S.statCard}>
            <div style={S.statNum}>{c.value}</div>
            <div style={S.statLbl}>{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ConversationsTab ─────────────────────────────────────────────────────────
function ConversationsTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [convos, setConvos]       = useState([]);
  const [selected, setSelected]   = useState(null);
  const [messages, setMessages]   = useState([]);
  const [replyText, setReplyText] = useState("");
  const [view, setView]           = useState("list");
  const [loading, setLoading]     = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState("");
  const unsubMessagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [doneConvos, setDoneConvos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kiranaos_done_convos") || "{}"); } catch { return {}; }
  });
  const toggleDone = (phone, e) => {
    e.stopPropagation();
    setDoneConvos((prev) => {
      const next = { ...prev, [phone]: !prev[phone] };
      localStorage.setItem("kiranaos_done_convos", JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "whatsapp_conversations"),
      (snap) => {
        const data = snap.docs
          .filter((d) => d.id.startsWith("91") && d.id.length === 12)
          .map((d) => ({ _id: d.id, ...d.data() }))
          .sort((a, b) => {
            return conversationActivityDate(b).getTime() - conversationActivityDate(a).getTime();
          });
        setConvos(data);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => () => {
    if (unsubMessagesRef.current) unsubMessagesRef.current();
  }, []);

  useEffect(() => {
    if (!msgLoading && selected) messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, msgLoading, selected]);

  const loadMessages = async (phone) => {
    if (unsubMessagesRef.current) unsubMessagesRef.current();
    setSelected(phone);
    setMsgLoading(true);
    const messagesQuery = query(collection(db, "whatsapp_conversations", phone, "messages"), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(
      messagesQuery,
      (snap) => {
      const msgs = snap.docs
        .map((d) => ({ _id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = timestampToDate(a.createdAt)?.getTime() || 0;
          const tb = timestampToDate(b.createdAt)?.getTime() || 0;
          return ta - tb;
        });
      setMessages(msgs);
        setMsgLoading(false);
      },
      (e) => {
        console.error(e);
        setMsgLoading(false);
      }
    );
    unsubMessagesRef.current = unsub;
  };

  const handleSendReply = async () => {
    const text = replyText.trim();
    if (!text || !selected || sendingReply) return;
    setSendingReply(true);
    setReplyError("");
    try {
      const currentUser = (auth || getAuth()).currentUser;
      const token = await currentUser?.getIdToken();
      if (!token) throw new Error("Admin login required");
      const res = await fetch(`${API_BASE}/api/admin/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: selected, message: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to send message");
      setReplyText("");
    } catch (e) {
      setReplyError(e.message || "Failed to send message");
    } finally {
      setSendingReply(false);
    }
  };

  if (loading) return <div style={S.spinner}>Loading conversations…</div>;
  const showList = !isMobile || view === "list";
  const showChat = !isMobile || view === "chat";
  const selectedConvo = convos.find((c) => c._id === selected);

  return (
    <div style={{ display: "flex", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 12 : 16, overflowX: "hidden", minHeight: 0 }}>
      {/* Left — conversation list */}
      {showList && (
        <div style={{
          width: isMobile ? "100%" : 240, flexShrink: 0,
          background: T.warmWhite, border: `1px solid ${T.border}`,
          borderRadius: 12, overflowY: "auto",
          maxHeight: isMobile ? "calc(100dvh - 150px)" : "75vh",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}>
          {convos.length === 0 && (
            <div style={{ ...S.empty, padding: "32px 16px" }}>No conversations yet</div>
          )}
          {convos.map((c) => (
            <div
              key={c._id}
              onClick={() => { loadMessages(c._id); if (isMobile) setView("chat"); }}
              style={{
                padding: isMobile ? "14px 16px" : "12px 16px",
                cursor: "pointer", fontSize: 13,
                fontWeight: selected === c._id ? 600 : 400,
                borderBottom: `1px solid ${T.border}`,
                background: selected === c._id ? "#fff3ed" : doneConvos[c._id] ? "#faf9f7" : "transparent",
                color: selected === c._id ? T.orange : doneConvos[c._id] ? "#bbb" : T.ink,
                opacity: doneConvos[c._id] ? 0.6 : 1,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "monospace", fontSize: 12 }}>+{c._id}</div>
                {c.leadFields?.storeName && (
                  <div style={{ marginTop: 2, color: "#aaa", fontSize: 11, fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {c.leadFields.storeName}
                  </div>
                )}
                {c.lastMessagePreview && (
                  <div style={{ marginTop: 5, color: "#8b8177", fontSize: 11, fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? "calc(100vw - 110px)" : 160 }}>
                    {c.lastMessageRole === "user" ? "User: " : "Kiran: "}{c.lastMessagePreview}
                  </div>
                )}
                {(c.lastMessageAt || c.updatedAt) && (
                  <div style={{ marginTop: 3, color: "#bbb", fontSize: 10, fontWeight: 500 }}>
                    {fmtMessageTime(c.lastMessageAt || c.updatedAt)}
                  </div>
                )}
              </div>
              <span
                onClick={(e) => toggleDone(c._id, e)}
                title={doneConvos[c._id] ? "Mark undone" : "Mark done"}
                style={{
                  fontSize: 15, cursor: "pointer", flexShrink: 0,
                  minWidth: 36, minHeight: 36, display: "grid", placeItems: "center",
                  color: doneConvos[c._id] ? T.green : T.border,
                  borderRadius: "50%",
                }}
              >
                ✓
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Right — chat thread */}
      {showChat && (
        <div style={{
          flex: 1, minWidth: isMobile ? 0 : 280,
          background: T.warmWhite, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: isMobile ? 12 : 18,
          display: "flex", flexDirection: "column",
          height: isMobile ? "calc(100dvh - 132px)" : undefined,
          maxHeight: isMobile ? undefined : "75vh",
          minHeight: 0, boxSizing: "border-box",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}>
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 10, borderBottom: `1px solid ${T.border}`, marginBottom: 10, flexShrink: 0 }}>
              <button
                type="button"
                aria-label="Back to conversations"
                onClick={() => setView("list")}
                style={{ width: 40, height: 40, display: "grid", placeItems: "center", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: 8, color: T.ink, fontSize: 20, cursor: "pointer", padding: 0, flexShrink: 0 }}
              >
                ←
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "monospace" }}>{selected ? `+${selected}` : "Conversation"}</div>
                {selectedConvo?.leadFields?.storeName && (
                  <div style={{ fontSize: 12, color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {selectedConvo.leadFields.storeName}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop chat header */}
          {!isMobile && selected && (
            <div style={{ paddingBottom: 12, marginBottom: 12, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace" }}>+{selected}</div>
              {selectedConvo?.leadFields?.storeName && (
                <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>{selectedConvo.leadFields.storeName}</div>
              )}
            </div>
          )}

          <div style={{ flex: 1, overflowY: "auto", paddingRight: 4, minHeight: 0 }}>
            {!selected && (
              <div style={{ ...S.empty, marginTop: 20 }}>Select a conversation to view messages</div>
            )}
            {msgLoading && <div style={S.spinner}>Loading messages…</div>}
            {!msgLoading && selected && messages.map((m) => (
              <div key={m._id} style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-start" : "flex-end" }}>
                <div style={{
                  maxWidth: isMobile ? "88%" : "68%",
                  padding: "9px 13px", borderRadius: 10, fontSize: 13, lineHeight: 1.55,
                  background: m.role === "user" ? "#f0ede8" : T.orange,
                  color: m.role === "user" ? T.ink : "#fff",
                  border: m.role === "user" ? `1px solid ${T.border}` : "none",
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {m.content && m.content.trim()
                    ? m.content
                    : (m.type === "media" || m.mediaType)
                      ? <span style={{ fontStyle: "italic", opacity: 0.8 }}>Media: {m.mediaType || "unknown"} message (not shown in chat)</span>
                      : <span style={{ fontStyle: "italic", opacity: 0.8 }}>(empty message)</span>}
                </div>
                <div style={{ fontSize: 11, color: "#bbb", marginTop: 3 }}>
                  {m.role === "user" ? "User" : "Kiran"} · {fmtMessageTime(m.createdAt)}
                  {m.whatsappStatus === "failed" && " · WhatsApp failed"}
                  {m.whatsappStatus === "sent" && " · WhatsApp sent"}
                </div>
              </div>
            ))}
            {!msgLoading && selected && messages.length === 0 && (
              <div style={{ ...S.empty, marginTop: 20 }}>No messages in this conversation</div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {selected && (
            <div style={{ position: "relative", display: "flex", gap: 8, paddingTop: 12, paddingBottom: isMobile ? "env(safe-area-inset-bottom)" : 0, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
              <textarea
                style={{ flex: 1, minWidth: 0, fontFamily: T.font, fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 8, padding: "9px 10px", outline: "none", resize: "none", height: isMobile ? 72 : 76, lineHeight: 1.5, boxSizing: "border-box" }}
                placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              />
              <button
                style={{ ...S.btn("primary"), minWidth: isMobile ? 64 : 56, minHeight: isMobile ? 44 : undefined, alignSelf: "flex-end" }}
                type="button"
                onClick={handleSendReply}
                disabled={sendingReply}
              >
                {sendingReply ? "Sending" : "Send"}
              </button>
              {replyError && (
                <div style={{ position: "absolute", left: 0, right: 0, bottom: isMobile ? 88 : 92, color: "#b42318", fontSize: 12, background: "#fff4f2", border: "1px solid #ffd0ca", borderRadius: 8, padding: "8px 10px" }}>
                  {replyError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TaskInboxWidget ──────────────────────────────────────────────────────────
function TaskInboxWidget({ getToken }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [rawText, setRawText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    const text = rawText.trim();
    if (!text || saving) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rawText: text }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");
      setRawText("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      setSaveError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...S.card(isMobile), marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Task Inbox</div>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
        Type a raw to-do or thought. The backend will classify it into Today. Limit: 5000 characters.
      </div>
      <textarea
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "10px 12px",
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          fontSize: 13,
          fontFamily: T.font,
          resize: "vertical",
          minHeight: 74,
          outline: "none",
          color: T.ink,
          background: "#fafaf9",
          lineHeight: 1.6,
        }}
        placeholder="Need to audit Kiran false promises from the last 7 days"
        value={rawText}
        rows={3}
        onChange={(e) => setRawText(e.target.value)}
      />
      <div style={{ fontSize: 11, color: rawText.length > 5000 ? T.red : "#aaa", marginTop: 6 }}>
        {rawText.length}/5000
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <button
          style={{ ...S.btn("primary"), opacity: (saving || !rawText.trim() || rawText.length > 5000) ? 0.5 : 1 }}
          type="button"
          disabled={saving || !rawText.trim() || rawText.length > 5000}
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save Task"}
        </button>
        {saveSuccess && <span style={{ fontSize: 12, color: T.green, fontWeight: 600 }}>Saved</span>}
      </div>
      {saveError && <div style={{ ...S.errorBanner, marginTop: 10 }}>{saveError}</div>}
    </div>
  );
}

// ─── CommandCenterTab ─────────────────────────────────────────────────────────
function CommandCenterTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingSnapshot, setRefreshingSnapshot] = useState(false);
  const [error, setError] = useState("");
  const [snapshotError, setSnapshotError] = useState("");
  const [tasks, setTasks] = useState([]);
  const [taskFilter, setTaskFilter] = useState("all");
  const [taskError, setTaskError] = useState("");
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const [lastRunId, setLastRunId] = useState("");

  const getToken = useCallback(async () => {
    const currentUser = (auth || getAuth()).currentUser;
    const token = await currentUser?.getIdToken();
    if (!token) throw new Error("Admin auth missing");
    return token;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/command-center`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load Command Center");
      setData(json);
    } catch (err) {
      setError(err.message || "Failed to load Command Center");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const saveSnapshot = useCallback(async () => {
    setRefreshingSnapshot(true);
    setSnapshotError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/command-center/snapshot`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to save snapshot");
      setData(json);
      setLastRunId(json.runId || "");
    } catch (err) {
      setSnapshotError(err.message || "Failed to save snapshot");
    } finally {
      setRefreshingSnapshot(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = query(
      collection(db, "business_tasks"),
      where("status", "in", ["todo", "doing", "blocked"])
    );
    const unsub = onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((taskDoc) => ({ id: taskDoc.id, ...taskDoc.data() })));
      setTaskError("");
    }, (err) => {
      console.error("Task listener failed:", err);
      setTaskError(err.message || "Task listener failed");
    });
    return unsub;
  }, []);

  const priorityRank = { P0: 0, P1: 1, P2: 2 };
  const statusRank = { blocked: 0, doing: 1, todo: 2 };
  const riskRank = { high: 0, medium: 1, low: 2 };

  const toTaskDate = (value) => {
    if (!value) return new Date(0);
    if (value.toDate) return value.toDate();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
  };

  const sortTasks = (list) => [...list].sort((a, b) => {
    const pDiff = (priorityRank[a.priority] ?? 1) - (priorityRank[b.priority] ?? 1);
    if (pDiff !== 0) return pDiff;
    const sDiff = (statusRank[a.status] ?? 2) - (statusRank[b.status] ?? 2);
    if (sDiff !== 0) return sDiff;
    const rDiff = (riskRank[a.riskLevel] ?? 1) - (riskRank[b.riskLevel] ?? 1);
    if (rDiff !== 0) return rDiff;
    return toTaskDate(a.createdAt) - toTaskDate(b.createdAt);
  });

  const handleStatusUpdate = async (taskId, status) => {
    setUpdatingTaskId(taskId);
    setTaskError("");
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Task update failed");
    } catch (err) {
      setTaskError(err.message || "Task update failed");
    } finally {
      setUpdatingTaskId(null);
    }
  };

  if (loading) return <div style={S.spinner}>Loading Command Center…</div>;

  if (error) return (
    <div style={S.card(isMobile)}>
      <div style={S.errorBanner}>{error}</div>
      <button style={S.btn("ghost")} type="button" onClick={load}>Retry</button>
    </div>
  );

  if (!data) return null;

  const { backend, stores, growth, risks, checkedAt } = data;

  const filteredTasks = sortTasks(tasks.filter((task) => {
    if (taskFilter === "doing") return task.status === "doing";
    if (taskFilter === "blocked") return task.status === "blocked";
    if (taskFilter === "p0") return task.priority === "P0";
    if (taskFilter === "human") return task.ownerType === "human";
    return true;
  }));
  const p0Count = tasks.filter((task) => task.priority === "P0").length;
  const p1Count = tasks.filter((task) => task.priority === "P1").length;
  const p2Count = tasks.filter((task) => task.priority === "P2").length;

  return (
    <div>
      <TaskInboxWidget getToken={getToken} />

      {/* Today View */}
      <div style={{ ...S.card(isMobile), marginBottom: 20 }}>
        <div style={{ ...S.cardHeader, marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Today</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
              {tasks.length} active - {p0Count} P0, {p1Count} P1, {p2Count} P2
            </div>
          </div>
          <select style={S.select} value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)}>
            <option value="all">All active</option>
            <option value="doing">Doing</option>
            <option value="blocked">Blocked</option>
            <option value="p0">P0 only</option>
            <option value="human">Human tasks</option>
          </select>
        </div>
        {taskError && <div style={S.errorBanner}>{taskError}</div>}
        {filteredTasks.length === 0 ? (
          <div style={S.empty}>
            {tasks.length === 0 ? "No active tasks. Add one above." : "No tasks match this filter."}
          </div>
        ) : (
          filteredTasks.map((task, idx) => {
            const priorityColors = {
              P0: [T.red, "#fee2e2"],
              P1: [T.orange, "#FDECE4"],
              P2: [T.inkSoft, "#f0ede9"],
            };
            const [priorityColor, priorityBg] = priorityColors[task.priority] || [T.inkSoft, "#f0ede9"];
            const updatedDate = toTaskDate(task.updatedAt);
            const daysSince = updatedDate.getTime() > 0
              ? Math.floor((Date.now() - updatedDate.getTime()) / 86400000)
              : 0;
            const isUpdating = updatingTaskId === task.id;
            const isLast = idx === filteredTasks.length - 1;
            return (
              <div
                key={task.id}
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "12px 0",
                  borderBottom: isLast ? "none" : `1px solid ${T.border}`,
                }}
              >
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", flexShrink: 0 }}>
                  <span style={S.pill(priorityBg, priorityColor)}>{task.priority || "P1"}</span>
                  <span style={S.pill("#f0ede9", T.inkSoft)}>{task.category || "Operations"}</span>
                  {task.ownerType === "human" && <span style={S.pill("#eff6ff", T.blue)}>Decision</span>}
                  {task.riskLevel === "high" && <span style={S.pill("#fee2e2", T.red)}>High risk</span>}
                  {task.status === "doing" && <span style={S.pill("#eff6ff", T.blue)}>In progress</span>}
                  {task.status === "blocked" && <span style={S.pill("#fee2e2", T.red)}>Blocked</span>}
                  {daysSince > 7 && <span style={S.pill("#FDECE4", T.orange)}>Stale {daysSince}d</span>}
                </div>
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  color: T.ink,
                  lineHeight: 1.55,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}>
                  {task.rawText || task.cleanedTitle || "Untitled task"}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                  {task.status !== "doing" && (
                    <button
                      style={{ ...S.btn("action"), opacity: isUpdating ? 0.5 : 1 }}
                      type="button"
                      disabled={isUpdating}
                      onClick={() => handleStatusUpdate(task.id, "doing")}
                    >
                      Start
                    </button>
                  )}
                  {task.status !== "blocked" && (
                    <button
                      style={{ ...S.btn("ghost"), opacity: isUpdating ? 0.5 : 1 }}
                      type="button"
                      disabled={isUpdating}
                      onClick={() => handleStatusUpdate(task.id, "blocked")}
                    >
                      Block
                    </button>
                  )}
                  <button
                    style={{ ...S.btn("success"), opacity: isUpdating ? 0.5 : 1 }}
                    type="button"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(task.id, "done")}
                  >
                    Done
                  </button>
                  <button
                    style={{ ...S.btn("ghost"), fontSize: 11, opacity: isUpdating ? 0.5 : 1 }}
                    type="button"
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate(task.id, "archived")}
                  >
                    Archive
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Platform */}
      {(() => {
        const hasFailures = stores.fail > 0 || !backend.ok;
        const borderColor = hasFailures ? T.red : T.green;
        return (
          <div style={{ ...S.card(isMobile), borderTop: `3px solid ${borderColor}`, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Platform</div>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: T.inkSoft }}>
                    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: backend.ok ? T.green : T.red, marginRight: 5, verticalAlign: "middle" }} />
                    API {backend.ok ? "up" : `down (${backend.statusCode})`}
                  </span>
                  <span style={{ fontSize: 12, color: T.inkSoft }}>
                    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: stores.demoStoreStatus === "PASS" ? T.green : T.red, marginRight: 5, verticalAlign: "middle" }} />
                    demo-store {stores.demoStoreStatus}
                  </span>
                  <span style={{ fontSize: 12, color: T.inkSoft }}>
                    {stores.pass}/{stores.total} stores healthy
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#bbb", marginTop: 5 }}>
                  Checked {new Date(checkedAt).toLocaleString("en-IN")}
                  {lastRunId ? ` · Snapshot ${lastRunId.slice(0, 8)}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {snapshotError && <span style={{ fontSize: 11, color: T.red }}>{snapshotError}</span>}
                <button style={S.btn("ghost")} type="button" onClick={load}>Reload</button>
                <button
                  style={{ ...S.btn("primary"), opacity: refreshingSnapshot ? 0.6 : 1 }}
                  type="button"
                  disabled={refreshingSnapshot}
                  onClick={saveSnapshot}
                >
                  {refreshingSnapshot ? "Saving..." : "Save Snapshot"}
                </button>
              </div>
            </div>
            {stores.fail > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Failing stores</div>
                {stores.failStores.map((s) => (
                  <div key={s.storeId} style={{ fontSize: 12, color: T.inkSoft, marginBottom: 4, paddingLeft: 8, borderLeft: `2px solid ${T.red}` }}>
                    <span style={{ fontWeight: 600 }}>{s.storeId}</span>{s.storeName ? ` (${s.storeName})` : ""}: {s.failures.join(", ")}
                  </div>
                ))}
              </div>
            )}
            {stores.missingOwnerStores && stores.missingOwnerStores.length > 0 && (
              <div style={{ fontSize: 12, color: T.orange, paddingLeft: 8, borderLeft: `2px solid ${T.orange}` }}>
                Login may be broken: {stores.missingOwnerStores.join(", ")} (missing owners doc)
              </div>
            )}
          </div>
        );
      })()}

      {/* Business */}
      <div style={{ ...S.card(isMobile), marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 12 }}>Business</div>
        <div style={S.statGrid}>
          <div style={S.statCard}>
            <div style={S.statNum}>{growth.recentOrders}</div>
            <div style={S.statLbl}>Orders this week</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statNum}>{growth.activeConversations}</div>
            <div style={S.statLbl}>Active chats (7d)</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statNum}>{growth.recentQualifiedLeads}</div>
            <div style={S.statLbl}>Leads (7d)</div>
          </div>
          <div style={S.statCard}>
            <div style={S.statNum}>{growth.whatsappConversations || 0}</div>
            <div style={S.statLbl}>Total WA convos</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 20, marginTop: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#bbb" }}>{growth.customers} customers total</span>
          <span style={{ fontSize: 11, color: "#bbb" }}>{growth.orders} orders total</span>
          <span style={{ fontSize: 11, color: "#bbb" }}>Kiran: +91 88603 10303 escalation · Instagram pending Meta review</span>
        </div>
      </div>

      {/* Open Items — only shown when there is something actionable */}
      {(growth.pendingOnboarding > 0 || risks.criticalOpen > 0 || risks.highOpen > 0) && (
        <div style={{ ...S.card(isMobile), marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 10 }}>Open Items</div>
          {growth.pendingOnboarding > 0 && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.orange, background: "#FDECE4", padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 1 }}>ONBOARDING</span>
              <div style={{ fontSize: 12, color: T.inkSoft }}>
                {growth.pendingOnboarding} store(s) waiting for review - see Onboarding tab
              </div>
            </div>
          )}
          {(risks.critical || []).map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.red, background: "#fee2e2", padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 1 }}>CRITICAL</span>
              <div style={{ fontSize: 12, color: T.inkSoft }}>
                {r.message}
                {r.action && <span style={{ color: "#bbb", display: "block", marginTop: 2 }}>{r.action}</span>}
              </div>
            </div>
          ))}
          {(risks.high || []).map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.orange, background: "#FDECE4", padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 1 }}>HIGH</span>
              <div style={{ fontSize: 12, color: T.inkSoft }}>
                {r.message}
                {r.action && <span style={{ color: "#bbb", display: "block", marginTop: 2 }}>{r.action}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Store Data Notes — catalogue context, not urgent */}
      {(stores.dataNotesStores || []).length > 0 && (() => {
        const missingImages = stores.dataNotesStores.filter((s) => s.notes.some((n) => n.includes("missing images") || n.includes("non-GCS")));
        const legacyCodes = stores.dataNotesStores.filter((s) => s.notes.some((n) => n.includes("missing product_code")));
        const legacyProductCount = legacyCodes.reduce((total, s) => {
          return total + s.notes
            .filter((n) => n.includes("missing product_code"))
            .reduce((count, n) => { const m = n.match(/(\d+)/); return count + (m ? parseInt(m[1], 10) : 0); }, 0);
        }, 0);
        return (
          <div style={{ ...S.card(isMobile), marginBottom: 20, borderLeft: `3px solid ${T.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.inkSoft, marginBottom: 4 }}>Store Data Notes</div>
            <div style={{ fontSize: 11, color: "#bbb", marginBottom: 12 }}>
              Catalogue context only. Use{" "}
              <code style={{ fontFamily: "monospace", background: "#f0ede9", padding: "1px 4px", borderRadius: 3 }}>
                node scripts/diagnose-store.js &lt;storeId&gt;
              </code>{" "}
              to investigate a specific store.
            </div>
            {missingImages.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#bbb", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Missing product images</div>
                <div style={{ fontSize: 12, color: "#bbb", marginBottom: 6 }}>
                  Products without images may affect visual presentation but do not block ordering.
                </div>
                {missingImages.map((s) => (
                  <div key={s.storeId} style={{ fontSize: 12, color: "#bbb", marginBottom: 3, paddingLeft: 8, borderLeft: `2px solid ${T.border}` }}>
                    <span style={{ fontWeight: 600, color: T.inkSoft }}>{s.storeId}</span>{s.storeName ? ` (${s.storeName})` : ""}:{" "}
                    {s.notes.filter((n) => n.includes("missing images") || n.includes("non-GCS")).join(", ")}
                  </div>
                ))}
              </div>
            )}
            {legacyCodes.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#bbb", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Legacy product codes
                  {legacyProductCount > 0 && (
                    <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: 6 }}>
                      ({legacyProductCount} products without code)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#bbb", marginBottom: 8 }}>
                  Pre-code stores. No action needed unless enabling inventory sync.
                </div>
                {legacyCodes.map((s) => (
                  <div key={s.storeId} style={{ fontSize: 12, color: "#bbb", marginBottom: 3, paddingLeft: 8, borderLeft: `2px solid ${T.border}` }}>
                    <span style={{ fontWeight: 600, color: T.inkSoft }}>{s.storeId}</span>{s.storeName ? ` (${s.storeName})` : ""}:{" "}
                    {s.notes.filter((n) => n.includes("missing product_code")).join(", ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Finance */}
      <div style={S.card(isMobile)}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 12 }}>Finance</div>
        <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 10 }}>
          <span style={{ fontWeight: 600 }}>Free until:</span> 31 December 2026. After that, one simple plan for all stores. No commission, no order caps.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ ...S.table, fontSize: 12 }}>
            <thead>
              <tr>{["Period", "Price", "Order caps", "Commission"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {[
                ["Until 31 Dec 2026", "₹0",        "None", "None"],
                ["After 31 Dec 2026", "₹35/month", "None", "None"],
              ].map(([period, price, caps, commission]) => (
                <tr key={period}>
                  <td style={S.td}>{period}</td>
                  <td style={S.td}>{price}</td>
                  <td style={S.td}>{caps}</td>
                  <td style={S.td}>{commission}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: "#bbb", marginTop: 10 }}>
          Active stores: {stores.total} · No live payment gateway · ₹35/month subscription billing (Razorpay) planned, not collecting yet
        </div>
      </div>
    </div>
  );
}

// ─── SuperAdminPage ───────────────────────────────────────────────────────────
function PhoneSearchTab() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    const digits = q.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Enter at least a 10-digit phone number");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = await (auth || getAuth()).currentUser?.getIdToken();
      if (!token) throw new Error("Admin auth missing");
      // POST with the number in the JSON body so it stays out of the URL,
      // browser history, and request logs.
      const res = await fetch(`${API_BASE}/api/admin/search-phone`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ phone: digits }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Search failed");
      setData(json);
    } catch (err) {
      setError(err.message || "Search failed");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const section = (title, count, body) => (
    <div style={S.card(isMobile)}>
      <div style={S.cardHeader}>
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <p style={S.cardMeta}>{count} found</p>
      </div>
      {count === 0 ? <p style={{ fontSize: 13, color: "#999", margin: 0 }}>No records.</p> : body}
    </div>
  );

  const totalHits = data
    ? data.ownerStores.length + data.onboarding.length + data.leads.length + data.customers.length + data.orders.length + (data.conversation ? 1 : 0)
    : 0;

  return (
    <div>
      <form onSubmit={search} style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Phone number — any format (98765 43210, +91…, 91…)"
          inputMode="tel"
          style={{ flex: "1 1 260px", padding: "10px 14px", fontSize: 14, borderRadius: 8, border: `1px solid ${T.border}`, fontFamily: T.font }}
        />
        <button type="submit" style={{ ...S.btn("primary"), padding: "10px 22px", fontSize: 13 }} disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>
      {error && <p style={{ color: "#b91c1c", fontSize: 13 }}>{error}</p>}

      {data && (
        <>
          <p style={{ fontSize: 13, color: "#777", marginBottom: 16 }}>
            Searched {data.query.variants.join(", ")} — {totalHits} record{totalHits === 1 ? "" : "s"} across all collections.
          </p>

          {section("Store owner", data.ownerStores.length, (
            <table style={S.table}><tbody>
              {data.ownerStores.map((s) => (
                <tr key={s.storeId}>
                  <td style={S.td}><strong>{s.storeName}</strong>{s.legacyOwnerRecord ? " (legacy owner record)" : ""}</td>
                  <td style={S.td}>{s.storeId}</td>
                  <td style={S.td}><a href={`/store/${s.storeId}`} target="_blank" rel="noreferrer">storefront</a></td>
                </tr>
              ))}
            </tbody></table>
          ))}

          {section("WhatsApp conversation", data.conversation ? 1 : 0, data.conversation && (
            <div>
              <p style={{ fontSize: 13, margin: "0 0 10px", color: "#777" }}>
                Last message {fmtDate(data.conversation.lastMessageAt)} ({data.conversation.lastMessageRole || "—"}): {data.conversation.lastMessagePreview || "—"}
              </p>
              {data.conversation.recentMessages.map((m, i) => (
                <p key={i} style={{ fontSize: 12, margin: "4px 0", color: m.role === "user" ? T.ink : "#777" }}>
                  <strong>{m.role || "?"}:</strong> {m.content || `[${m.type || "non-text"}]`}
                </p>
              ))}
            </div>
          ))}

          {section("Onboarding requests", data.onboarding.length, (
            <table style={S.table}><tbody>
              {data.onboarding.map((r) => (
                <tr key={r.id}>
                  <td style={S.td}><strong>{r.ownerName || "—"}</strong></td>
                  <td style={S.td}>{r.storeName || "—"}</td>
                  <td style={S.td}>{r.status || "—"}</td>
                  <td style={S.td}>{fmtDate(r.createdAt)}</td>
                </tr>
              ))}
            </tbody></table>
          ))}

          {section("Customer at stores", data.customers.length, (
            <table style={S.table}><tbody>
              {data.customers.map((c) => (
                <tr key={`${c.storeId}-${c.phone}`}>
                  <td style={S.td}><strong>{c.name || "—"}</strong></td>
                  <td style={S.td}>{c.storeName}</td>
                  <td style={S.td}>{c.totalOrders} orders · ₹{Math.round(c.totalSpent || 0).toLocaleString("en-IN")}</td>
                  <td style={S.td}>last {fmtDate(c.lastOrderAt)}</td>
                </tr>
              ))}
            </tbody></table>
          ))}

          {section("Orders", data.orders.length, (
            <table style={S.table}><tbody>
              {data.orders.map((o) => (
                <tr key={`${o.storeId}-${o.orderId}`}>
                  <td style={S.td}>{fmtDate(o.createdAt)}</td>
                  <td style={S.td}><strong>{o.storeName}</strong></td>
                  <td style={S.td}>₹{Math.round(o.total || 0).toLocaleString("en-IN")}</td>
                  <td style={S.td}>{o.status || "—"}</td>
                  <td style={S.td}>notif: {o.notificationStatus || "—"}</td>
                </tr>
              ))}
            </tbody></table>
          ))}

          {section("Leads", data.leads.length, (
            <table style={S.table}><tbody>
              {data.leads.map((l) => (
                <tr key={l.id}>
                  <td style={S.td}><strong>{l.name || "—"}</strong></td>
                  <td style={S.td}>{l.source || "—"}</td>
                  <td style={S.td}>{fmtDate(l.createdAt)}</td>
                </tr>
              ))}
            </tbody></table>
          ))}
        </>
      )}
    </div>
  );
}

const TABS = ["Command Center", "Phone Search", "Onboarding", "Stores", "Customers", "Orders", "Analytics", "Conversations"];

export default function SuperAdminPage() {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [tab, setTab] = useState("Command Center");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    const activeAuth = auth || getAuth();
    getRedirectResult(activeAuth).catch((err) => {
      if (err?.code !== "auth/no-auth-event") {
        setLoginError(err?.message || "Sign-in failed. Please try again.");
      }
    });
    return onAuthStateChanged(activeAuth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
  }, []);

  const handleLogin = async () => {
    setLoginError("");
    const activeAuth = auth || getAuth();
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(activeAuth, provider);
    } catch (err) {
      if (err?.code === "auth/popup-blocked" || err?.code === "auth/popup-closed-by-user" || err?.code === "auth/cancelled-popup-request") {
        await signInWithRedirect(activeAuth, provider);
        return;
      }
      setLoginError(err?.message || "Sign-in failed. Please try again.");
    }
  };

  if (authLoading) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#bbb", fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ width: "100%", maxWidth: 360, margin: "0 16px" }}>
          <div style={{ background: T.warmWhite, borderRadius: 16, border: `1px solid ${T.border}`, padding: "36px 28px", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, marginBottom: 4 }}>KiranaOS</div>
            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 28, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.6 }}>Admin Console</div>
            <button
              style={{ ...S.btn("primary"), padding: "11px 20px", fontSize: 14, width: "100%", borderRadius: 10 }}
              type="button"
              onClick={handleLogin}
            >
              Sign in with Google
            </button>
            {loginError && (
              <div style={{ marginTop: 14, ...S.errorBanner, textAlign: "left" }}>
                {loginError}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (user.email !== "nishant2goldy@gmail.com") {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div style={{ width: "100%", maxWidth: 400, margin: "0 16px" }}>
          <div style={{ background: T.warmWhite, borderRadius: 16, border: `1px solid ${T.border}`, padding: "32px 28px", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.red, marginBottom: 8 }}>Access Denied</div>
            <div style={{ fontSize: 13, color: "#aaa", marginBottom: 20 }}>{user.email}</div>
            <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 24 }}>This account is not authorised to access the admin console.</div>
            <button style={S.btn("ghost")} type="button" onClick={() => (auth || getAuth()).signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header(isMobile)}>
        <div style={S.headerLeft}>
          <span style={S.logo}>KiranaOS</span>
          <span style={S.badge}>Admin</span>
        </div>
        <div style={S.headerRight(isMobile)}>
          <span style={S.headerEmail(isMobile)}>{user.email}</span>
          <button style={{ ...S.btn("ghost"), fontSize: 12 }} type="button" onClick={() => (auth || getAuth()).signOut()}>
            Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs(isMobile)}>
        {TABS.map((t) => (
          <button key={t} style={S.tab(tab === t, isMobile)} type="button" onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {/* Body */}
      <div style={S.body(isMobile)}>
        {tab === "Command Center" && <CommandCenterTab />}
        {tab === "Phone Search"  && <PhoneSearchTab />}
        {tab === "Onboarding"    && <OnboardingTab />}
        {tab === "Stores"        && <StoresTab />}
        {tab === "Customers"     && <CustomersTab />}
        {tab === "Orders"        && <OrdersTab />}
        {tab === "Analytics"     && <AnalyticsTab />}
        {tab === "Conversations" && <ConversationsTab />}
      </div>
    </div>
  );
}
